/**
 * The Hammer change-impact machinery as pure async helpers, extracted from the
 * old HammerDialog so the Sync design dialog can run it in both of its modes:
 *
 *   Stage 1 (planRipple): one strict-JSON call → which sections change + why.
 *   Stage 2 (draftSections): one structured-markdown call per planned section,
 *           in parallel, parsed with parseSectionResponse.
 *   applyProposals: write the accepted drafts via design:writeSection.
 *
 * Stage 1 uses one-shot `aiChat` (not streaming): the plan is short JSON that
 * must be parsed whole, and aiChat inherits sendMessage's retry/backoff.
 */
import type {
  DesignChangeSummary,
  DesignConflict,
  DesignProject,
} from '@volley/crucible-ipc-types';
import { aiChat, readLens, readSection, writeSection } from '../anvil-api';
import {
  HAMMER_CONFLICT_INSTRUCTIONS,
  HAMMER_GUIDE,
  HAMMER_STAGE1_INSTRUCTIONS,
  ITEM_MODEL_GUIDE,
} from '../prompts';
import { DesignConflictPlanSchema, HammerPlanSchema, parseAiJson } from '../schemas';
import {
  SECTION_OUTPUT_INSTRUCTIONS,
  ITEM_SECTION_OUTPUT_INSTRUCTIONS,
  parseSectionResponse,
} from './section-response';
import {
  itemModeFor,
  mergeItemsMarkdown,
  appendFreeformItem,
  parseItems,
  isGameLoopSection,
} from './section-items';
import { slugifyToKebab } from '../../slugify';

export interface LensRef {
  id: string;
  kind: 'agent' | 'skill';
}

export interface RipplePlanEntry {
  sectionId: string;
  diff?: string;
  rationale?: string;
}

export interface RippleProposal {
  sectionId: string;
  sectionTitle: string;
  originalBody: string;
  newBody: string;
  diff: string;
  impacts: string;
  risks: string;
  benefits: string;
  accepted: boolean;
  applyable: boolean;
  error?: string;
}

export class StaleRippleProposalError extends Error {
  readonly staleSectionTitles: string[];

  constructor(staleSectionTitles: string[]) {
    super(
      `Proposal is stale. Refresh and regenerate before applying: ${staleSectionTitles.join(', ')}`,
    );
    this.name = 'StaleRippleProposalError';
    this.staleSectionTitles = staleSectionTitles;
  }
}

export async function buildLensBlock(lensIds: LensRef[]): Promise<string> {
  if (lensIds.length === 0) return '';
  const parts: string[] = ['\n\n# Active lenses\n'];
  for (const { id, kind } of lensIds) {
    try {
      const full = await readLens(id, kind);
      parts.push(`\n## Lens: ${full.name}\n${full.description}\n\n${full.body}\n`);
    } catch {
      // Skip an unreadable lens.
    }
  }
  return parts.join('');
}

/**
 * Synthesize a stage-1 "change idea" from the pending-changes summary, so the
 * confirmation-mode sync runs a REAL ripple pass over what actually changed
 * since the last build rather than a canned mapping.
 */
export function synthesizePendingIdea(summary: DesignChangeSummary): string {
  const lines: string[] = ['These design sections were edited and are the source of truth:'];
  for (const title of summary.sections.changed) lines.push(`- ${title} (edited)`);
  for (const title of summary.sections.added) lines.push(`- ${title} (new section)`);
  for (const title of summary.sections.removed) lines.push(`- ${title} (removed)`);
  if (summary.screens.changed.length || summary.screens.added.length) {
    lines.push(
      `Screens already updated: ${[...summary.screens.changed, ...summary.screens.added].join(', ')}.`,
    );
  }
  lines.push(
    'Propagate the implications of those edits across the REST of the design so every section agrees with them. Do not rewrite the edited sections themselves unless they contradict each other.',
  );
  return lines.join('\n');
}

/** Kebab a topic into a stable resolution key (never empty). */
function conflictId(topic: string): string {
  return slugifyToKebab(topic) || 'conflict';
}

/**
 * PRE-RIPPLE — detect DIRECT contradictions between the edited source-of-truth
 * sections before propagating them. The sync filters the edited sections out of
 * its ripple targets (they ARE the source of truth), so a disagreement between
 * two edited docs — "8 players" in Overview vs "6 players" in Systems — would
 * otherwise survive untouched. Surfacing it lets the designer pick a winner
 * before anything is written (#1936).
 *
 * Returns `[]` when fewer than two sections were edited (no possible
 * cross-section contradiction) or when the edited sections are consistent.
 * Best-effort: the caller treats a thrown error as "no conflicts" and proceeds.
 */
export async function detectConflicts(opts: {
  gameId: string;
  project: DesignProject;
  /** Ids of the edited (source-of-truth) sections — those filtered out of the
   *  ripple targets. Conflicts among them are what this pass guards against. */
  editedSectionIds: string[];
  lensBlock: string;
}): Promise<DesignConflict[]> {
  const { gameId, project, editedSectionIds, lensBlock } = opts;
  const edited = project.sections.filter((s) => editedSectionIds.includes(s.id));
  if (edited.length < 2) return [];

  const docs = await Promise.all(
    edited.map(async (s) => {
      let body = '';
      try {
        body = await readSection(gameId, s.filename ?? `${s.id}.md`);
      } catch {
        // Unreadable / new section — treat as empty; it can still conflict via title.
      }
      return { id: s.id, title: s.title, body };
    }),
  );

  const dump = docs.map((d) => `### ${d.id} — ${d.title}\n${d.body || '(empty)'}`).join('\n\n');
  const system = `${HAMMER_GUIDE}${lensBlock}\n\n${HAMMER_CONFLICT_INSTRUCTIONS}`;
  const user = `Project: ${project.name} (${project.projectType.toUpperCase()})\n\nThe designer edited these sections at once; treat them as competing sources of truth and find DIRECT contradictions between them:\n\n${dump}`;
  const resp = await aiChat({
    gameId,
    system,
    messages: [{ role: 'user', content: user }],
    maxTokens: 2048,
  });

  const plan = parseAiJson(resp.text, DesignConflictPlanSchema);
  const editedIds = new Set(edited.map((s) => s.id));
  return (
    (plan.conflicts ?? [])
      .map((c, i) => {
        // Keep only claims that point at a real edited section; the model can echo
        // a stale or hallucinated id.
        const claims = (c.claims ?? [])
          .filter((cl) => editedIds.has(cl.sectionId))
          .map((cl) => ({
            sectionId: cl.sectionId,
            sectionTitle:
              project.sections.find((s) => s.id === cl.sectionId)?.title ?? cl.sectionId,
            claim: cl.claim ?? '',
          }));
        return {
          // Suffix the index so two conflicts with the same topic don't collide on
          // one resolution key / radio group.
          id: `${conflictId(c.topic)}-${i}`,
          topic: c.topic,
          claims,
          ...(c.suggestion ? { suggestion: c.suggestion } : {}),
        };
      })
      // A real contradiction needs at least two distinct sections disagreeing.
      .filter((c) => new Set(c.claims.map((cl) => cl.sectionId)).size >= 2)
  );
}

/**
 * Fold the designer's conflict resolutions into the ripple idea so the sync
 * pass propagates the WINNING claim and corrects the losing sections. Empty
 * when nothing has been resolved.
 *
 * `resolutions` maps a conflict id → the winning section id.
 */
export function describeConflictResolutions(
  conflicts: DesignConflict[],
  resolutions: Record<string, string>,
): string {
  const lines: string[] = [];
  for (const c of conflicts) {
    const winnerId = resolutions[c.id];
    const winner = c.claims.find((cl) => cl.sectionId === winnerId);
    if (!winner) continue;
    const losers = c.claims.filter((cl) => cl.sectionId !== winnerId);
    lines.push(
      `- ${c.topic}: the correct value is "${winner.claim}" (from ${winner.sectionTitle}). ` +
        `Update ${losers.map((l) => l.sectionTitle).join(', ')} to match and remove the contradiction.`,
    );
  }
  if (lines.length === 0) return '';
  return ['', 'RESOLVED CONTRADICTIONS (treat these decisions as canonical):', ...lines].join('\n');
}

/**
 * The section ids that LOST a resolved conflict and so must be corrected by the
 * ripple — even though the designer edited them and they'd normally be excluded
 * from ripple targets as source-of-truth docs.
 */
export function conflictLoserSectionIds(
  conflicts: DesignConflict[],
  resolutions: Record<string, string>,
): string[] {
  const losers = new Set<string>();
  for (const c of conflicts) {
    const winnerId = resolutions[c.id];
    if (!winnerId) continue;
    for (const cl of c.claims) if (cl.sectionId !== winnerId) losers.add(cl.sectionId);
  }
  return [...losers];
}

/** STAGE 1 — plan which sections the change ripples into (strict JSON). */
export async function planRipple(opts: {
  gameId: string;
  project: DesignProject;
  idea: string;
  lensBlock: string;
}): Promise<RipplePlanEntry[]> {
  const { gameId, project, idea, lensBlock } = opts;
  const sectionDump = project.sections
    .map((s) => `- ${s.id} — ${s.title} [${s.doc.toUpperCase()}]`)
    .join('\n');
  const planSystem = `${HAMMER_GUIDE}${lensBlock}\n\n${HAMMER_STAGE1_INSTRUCTIONS}`;
  const planUser = `Project: ${project.name} (${project.projectType.toUpperCase()})\n\nProposed change: ${idea}\n\nCurrent sections:\n${sectionDump}`;
  const planResp = await aiChat({
    gameId,
    system: planSystem,
    messages: [{ role: 'user', content: planUser }],
    maxTokens: 4096,
  });
  const plan = parseAiJson(planResp.text, HammerPlanSchema);
  return (plan.sections ?? []).filter((s) => project.sections.some((ps) => ps.id === s.sectionId));
}

/** STAGE 2 — draft each planned section in parallel (structured markdown). */
export async function draftSections(opts: {
  gameId: string;
  project: DesignProject;
  idea: string;
  lensBlock: string;
  planned: RipplePlanEntry[];
}): Promise<RippleProposal[]> {
  const { gameId, project, idea, lensBlock, planned } = opts;
  const draftResults = await Promise.allSettled(
    planned.map(async (entry): Promise<RippleProposal> => {
      const meta = project.sections.find((s) => s.id === entry.sectionId);
      const filename = meta?.filename ?? `${entry.sectionId}.md`;
      let originalBody = '';
      try {
        originalBody = await readSection(gameId, filename);
      } catch {
        // New / unreadable section — treat as empty baseline.
      }
      // Item-mode sections (Systems/Content/Art Direction) are a list of cards,
      // not a freeform doc — draft ITEMS and merge them in by name so the ripple
      // doesn't restate the section title as an item or clobber untouched items.
      const isItems = meta ? itemModeFor(meta) : false;
      const planBlock = `Stage-1 plan for this section:\n- Diff: ${entry.diff ?? ''}\n- Rationale: ${entry.rationale ?? ''}`;
      const secSystem = isItems
        ? `${HAMMER_GUIDE}${lensBlock}\n\n${ITEM_MODEL_GUIDE}\n\nThis is STAGE 2: revise the "${meta?.title ?? entry.sectionId}" item section. Return ONLY the item(s) to add or change to reflect the plan — the app merges them into the section by name and preserves the others. Do NOT restate the section title as an item, and do NOT re-output unchanged items.\n\n${planBlock}\n\n${ITEM_SECTION_OUTPUT_INSTRUCTIONS}`
        : `${HAMMER_GUIDE}${lensBlock}\n\nThis is STAGE 2: draft ONE revised section's full markdown.\n\nSection to revise: ${entry.sectionId} — ${meta?.title ?? entry.sectionId} [${(meta?.doc ?? 'gdd').toUpperCase()}]\n${planBlock}\n\n${SECTION_OUTPUT_INSTRUCTIONS}`;
      const secUser = `Designer's overall change idea: ${idea}\n\nCurrent body of ${entry.sectionId}:\n\n${originalBody || '(no current body)'}`;
      const resp = await aiChat({
        gameId,
        system: secSystem,
        messages: [{ role: 'user', content: secUser }],
        maxTokens: 8192,
      });

      if (isItems) {
        const reply = resp.text.trim();
        const hasItems = parseItems(reply).items.length > 0;
        // Merge the drafted item(s) by name, preserving the rest. If the model
        // answered in prose instead of items (no ## headings), don't discard it
        // — capture it as a labelled freeform item so the change is still
        // reviewable and applyable (the diff shows the captured text in green).
        const newBody = hasItems
          ? mergeItemsMarkdown(originalBody, reply, {
              pinGameLoop: isGameLoopSection(entry.sectionId),
            })
          : appendFreeformItem(originalBody, reply, 'Revision notes');
        return {
          sectionId: entry.sectionId,
          sectionTitle: meta?.title ?? entry.sectionId,
          originalBody,
          newBody,
          diff: entry.diff ?? '',
          impacts: entry.rationale ?? '',
          risks: '',
          benefits: '',
          accepted: true,
          applyable: true,
        };
      }

      const parsed = parseSectionResponse(resp.text);
      const applyable = parsed.body.trim().length > 0;
      return {
        sectionId: entry.sectionId,
        sectionTitle: meta?.title ?? entry.sectionId,
        originalBody,
        newBody: applyable
          ? parsed.body
          : `(AI returned no body — first 200 chars: ${resp.text.slice(0, 200)})`,
        diff: entry.diff ?? '',
        impacts: parsed.impacts,
        risks: parsed.risks,
        benefits: parsed.benefits,
        accepted: applyable,
        applyable,
        ...(applyable
          ? {}
          : { error: 'AI returned no section body; this proposal cannot be applied.' }),
      };
    }),
  );
  return draftResults.map((result, index): RippleProposal => {
    if (result.status === 'fulfilled') return result.value;
    const entry = planned[index];
    const meta = entry ? project.sections.find((s) => s.id === entry.sectionId) : undefined;
    return {
      sectionId: entry?.sectionId ?? `section-${index}`,
      sectionTitle: meta?.title ?? entry?.sectionId ?? `Section ${index + 1}`,
      originalBody: '',
      newBody: '',
      diff: entry?.diff ?? '',
      impacts: '',
      risks: '',
      benefits: '',
      accepted: false,
      applyable: false,
      error:
        result.reason instanceof Error
          ? result.reason.message
          : `Failed to draft section: ${String(result.reason)}`,
    };
  });
}

/** Write the accepted, applyable proposals via design:writeSection. */
export async function applyProposals(opts: {
  gameId: string;
  project: DesignProject;
  proposals: RippleProposal[];
}): Promise<void> {
  const { gameId, project, proposals } = opts;
  const accepted = proposals.filter((x) => x.accepted && x.applyable);
  const targets = accepted.map((proposal) => {
    const meta = project.sections.find((section) => section.id === proposal.sectionId);
    return {
      proposal,
      filename: meta?.filename ?? `${proposal.sectionId}.md`,
      title: meta?.title ?? proposal.sectionTitle,
    };
  });

  const staleTitles: string[] = [];
  for (const target of targets) {
    let currentBody = '';
    try {
      currentBody = await readSection(gameId, target.filename);
    } catch {
      currentBody = '';
    }
    if (currentBody !== target.proposal.originalBody) {
      staleTitles.push(target.title);
    }
  }
  if (staleTitles.length > 0) {
    throw new StaleRippleProposalError(staleTitles);
  }

  for (const { proposal, filename } of targets) {
    await writeSection(gameId, filename, proposal.newBody);
  }
}
